import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useOutletContext } from "react-router-dom";
import { MaterialReactTable } from "material-react-table";
import CountUp from "react-countup";
import { decodeHtml } from "../../utils/decodeHtml";
import RiskDetailModal from "../../components/Modals/workstation/RiskDetail";

function WorkstationList() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const { setGlobalLoading, token } = useOutletContext();
  const [options, setoptions] = useState([]);
  const [total_roles, settotal_roles] = useState(null);
  const [active_roles, setactive_roles] = useState(null);
  const [closed_roles, setclosed_roles] = useState(null);
  const [userInfo, setUserInfo] = useState({});
  const [inactive_roles, setinactive_roles] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRoleData, setSelectedRoleData] = useState(null);
  const [currentView, setCurrentView] = useState("active");

  const fetchOptions = async () => {
    setGlobalLoading(true); //  Show loader before API call
    try {
      const response = await axios.get(
        `${API_BASE_URL}/workstation/get_role_list/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
    // Log the result
      setactive_roles(response.data.active_roles || []);
      settotal_roles(response.data.total_roles || []);
      setclosed_roles(response.data.closed_roles || []);
      setinactive_roles(response.data.inactive_roles || []);
      setUserInfo(response.data.user_info || {});
    } catch (error) {
      console.error("API Error:", error);
      setoptions([]); // Prevent errors from breaking UI
      setGlobalLoading(false);
    } finally {
      setGlobalLoading(false);
    }
  };

  useEffect(() => {
    setoptions(active_roles || []);
  }, [active_roles]);

  useEffect(() => {
    if (options.length > 0) return;
    fetchOptions();
  }, []);

  // Get current date
  // Compute fiscal year start and end dynamically
  const financial_year = () => {
    const now = new Date();
    const current_month = now.getMonth(); // 0-based: 0=Jan

    let current_year = now.getFullYear();
    let from = "";
    let to = "";

    if (current_month >= 3) {
      // April or later
      const next_year = current_year + 1;
      from = `${current_year}-04-01`;
      to = `${next_year}-03-31`;
    } else {
      // Jan-Mar
      const previous_year = current_year - 1;
      from = `${previous_year}-04-01`;
      to = `${current_year}-03-31`;
    }

    return [new Date(from), new Date(to)];
  };
  const normalizeToLocalDate = (dateInput) => {
    if (!dateInput) return null;

    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return null;

    // Convert to local date only (no time)
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  const filterRolesByFiscalYear = (roles) => {
    if (userInfo.admin === 1 || userInfo.designation_id === 1) {
      return roles || [];
    }

    const [fiscalStart, fiscalEnd] = financial_year();

    // Normalize FY boundaries too
    const fyStart = new Date(
      fiscalStart.getFullYear(),
      fiscalStart.getMonth(),
      fiscalStart.getDate(),
    );

    const fyEnd = new Date(
      fiscalEnd.getFullYear(),
      fiscalEnd.getMonth(),
      fiscalEnd.getDate(),
    );

    return (roles || []).filter((role) => {
      if (role.status === "Active") {
        return true;
      }
      const roleDate = normalizeToLocalDate(role.change_status);

      if (!roleDate) return false;
      return roleDate >= fyStart && roleDate <= fyEnd;
    });
  };

  // In show functions
  const showTotalRoles = () => {
    setGlobalLoading(true);
    try {
      setoptions(filterRolesByFiscalYear(total_roles));
      setCurrentView("total");
    } finally {
      setGlobalLoading(false);
    }
  };

  const showClosedRoles = () => {
    setGlobalLoading(true);
    try {
      setoptions(filterRolesByFiscalYear(closed_roles));
      setCurrentView("closed");
    } finally {
      setGlobalLoading(false);
    }
  };
  // ... after showClosedRoles ...

  const showInactiveRoles = () => {
    setGlobalLoading(true);
    try {
      setoptions(filterRolesByFiscalYear(inactive_roles)); // Apply fiscal year filter
      setCurrentView("inactive");
    } finally {
      setGlobalLoading(false);
    }
  };

  const showActiveRoles = async () => {
    try {
      setGlobalLoading(true);
      setoptions(active_roles);
      setCurrentView("active");
    } catch (error) {
      console.log(error);
      setGlobalLoading(false);
    } finally {
      setGlobalLoading(false);
    }
  };

  const clampPercent = (value) => {
    const v = parseFloat(value);
    if (isNaN(v)) return 0;
    return Math.max(0, Math.min(v, 100));
  };

  // --- NEW MODAL HANDLERS ---
  const handleViewRiskDetails = (roleData) => {
    // roleData contains all calculated and original data for the row
    setSelectedRoleData(roleData);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRoleData(null);
  };

  // ... after handleCloseModal ...

  const baseColumns = [
    {
      accessorKey: "reference",
      header: "Role ID",
      size: 50,
    },
    {
      accessorKey: "name",
      header: "Role Name",
      size: 200,
      Cell: ({ row }) => (
        <Link
          to={`/workstation/view/${row.original.id}`}
        >
          <b>{decodeHtml(row.original.name)}</b>
        </Link>
      ),
    },
    {
      accessorKey: "company",
      header: "Client",
      size: 150,
    },
    {
      accessorKey: "role_manager",
      header: "Role Manager",
      size: 70,
    },
    {
      accessorKey: "type",
      header: "Role Type",
      size: 70,
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 70,
    },
    {
      accessorKey: "created_date",
      header: "Role Start",
      size: 70,
    },
  ];

  // Define the Role Risk column separately
  const riskColumn = {
    accessorKey: "weighted_points",
    enableSorting: true,
    header: (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          textAlign: "center",
          fontWeight: "bold",
          whiteSpace: "nowrap",
        }}
      >
        <span style={{ fontSize: "14px" }}>Role Risk%</span>
      </div>
    ),
    size: 170,
    Cell: ({ row, cell }) => {
      const value = parseFloat(cell.getValue() || 0);
      return (
        <span
          onClick={() => handleViewRiskDetails(row.original)}
          style={{
            color: value >= 50 ? "red" : "black",
            fontWeight: "bold",
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          {value.toFixed(1)}
        </span>
      );
    },
  };

  // 💡 Dynamically build the final columns array
  const finalColumns =
    currentView === "active"
      ? [...baseColumns.slice(0, 5), riskColumn, ...baseColumns.slice(5)] // Insert risk column before status/date
      : baseColumns;


  return (
    <>
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Workstation</h2>
          </div>
          <Link
            to="/workstation/performancebyyear"
            style={{
              textDecoration: "none",
              border: "1px solid #22c55e",
              color: "#22c55e",
              padding: "6px 16px",
              borderRadius: "20px",
              fontWeight: 500,
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            👁 View Previous FY Performance
          </Link>
        </div>
        <div className="row">
          <div
            className="col-xl-3 col-md-6 d-flex"
            style={{ cursor: "pointer" }}
            onClick={() => showTotalRoles()}
          >
            <div className="card flex-fill">
              <div className="card-body my-card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0 me-2">
                      <span className="p-2 br-10 bg-pink-transparent border border-pink d-flex align-items-center justify-content-center">
                        <i className="ti ti-users-group text-pink fs-18"></i>
                      </span>
                    </div>
                    <div>
                      <p className="fs-12 fw-medium mb-0 text-gray-5 mb-1">
                        Total Roles
                      </p>
                      <h4 className="counter">
                        <CountUp
                          start={0}
                          end={filterRolesByFiscalYear(total_roles).length}
                          duration={3}
                        />
                      </h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className="col-xl-3 col-md-6 d-flex"
            style={{ cursor: "pointer" }}
            onClick={() => showActiveRoles()}
          >
            <div className="card flex-fill">
              <div className="card-body my-card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0 me-2">
                      <span className="p-2 br-10 bg-success-transparent border border-success d-flex align-items-center justify-content-center">
                        <i className="ti ti-user-share fs-18"></i>
                      </span>
                    </div>
                    <div>
                      <p className="fs-12 fw-medium mb-0 text-gray-5 mb-1">
                        Active Roles
                      </p>
                      <h4 className="counter">
                        <CountUp
                          start={0}
                          end={
                            Array.isArray(active_roles)
                              ? active_roles.length
                              : (active_roles ?? 0)
                          }
                          duration={3}
                        />
                      </h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className="col-xl-3 col-md-6 d-flex"
            style={{ cursor: "pointer" }}
            onClick={() => showClosedRoles()}
          >
            <div className="card flex-fill">
              <div className="card-body my-card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0 me-2">
                      <span className="p-2 br-10 bg-info-transparent border border-info d-flex align-items-center justify-content-center">
                        <i className="ti ti-user-plus fs-18"></i>
                      </span>
                    </div>
                    <div>
                      <p className="fs-12 fw-medium mb-0 text-gray-5 mb-1">
                        Closed Roles
                      </p>
                      <h4 className="counter">
                        <CountUp
                          start={0}
                          end={filterRolesByFiscalYear(closed_roles).length}
                          duration={3}
                        />
                      </h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className="col-xl-3 col-md-6 d-flex"
            style={{ cursor: "pointer" }}
            onClick={() => showInactiveRoles()}
          >
            <div className="card flex-fill">
              <div className="card-body my-card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0 me-2">
                      <span className="p-2 br-10 bg-warning-transparent border border-warning d-flex align-items-center justify-content-center">
                        <i className="ti ti-lock text-warning fs-18"></i>{" "}
                        {/* Using 'ti ti-lock' or similar for inactive */}
                      </span>
                    </div>
                    <div>
                      <p className="fs-12 fw-medium mb-0 text-gray-5 mb-1">
                        Inactive Roles
                      </p>
                      <h4 className="counter">
                        <CountUp
                          start={0}
                          end={filterRolesByFiscalYear(inactive_roles).length} // << USING inactive_roles
                          duration={3}
                        />
                      </h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body p-0">
            <div className="custom-datatable-filter table-responsive">
              <MaterialReactTable
                columns={finalColumns}
                data={options.map((data) => {
                  // same formula as in your colum

                  const metrics = [
                    {
                      value: clampPercent(
                        data.matchingMatric?.[0]
                          ?.briefed_dropped_vs_profiles_added_pct,
                      ),
                      threshold: 30,
                      weight: 0.1,
                      below: true,
                    },
                    {
                      value:
                        data.matchingMatric?.[0]?.brief_to_eval_conversion_pct,
                      threshold: 30,
                      weight: 0.1,
                      below: true,
                    },
                    {
                      value:
                        data.matchingMatric?.[0]?.recommend_to_shortlist_pct,
                      threshold: 60,
                      weight: 0.1,
                      below: true,
                    },
                    {
                      value:
                        data.matchingMatric?.[0]
                          ?.drop_percentage_of_profiles_added,
                      threshold: 60,
                      weight: 0.1,
                      below: false,
                    },
                    {
                      value:
                        data.matchingMatric?.[0]
                          ?.interview_setup_to_complete_pct,
                      threshold: 70,
                      weight: 0.1,
                      below: true,
                    },
                    {
                      value:
                        data.matchingMatric?.[0]?.last_brief_within_1_weeks,
                      weight: 0.15,
                      type: "yesRisk",
                    },
                    {
                      value:
                        data.matchingMatric?.[0]
                          ?.evaluated_no_recent_recommendation,
                      weight: 0.2,
                      type: "noRisk",
                    },
                    {
                      value:
                        data.matchingMatric?.[0]
                          ?.interview_done_no_offer_closed_10_days,
                      weight: 0.15,
                      type: "yesRisk",
                    },
                  ];

                  const computeSubScore = (item) => {
                    if (item.type === "yesRisk")
                      return (item.value || "").toUpperCase() === "YES"
                        ? 100
                        : 0;
                    if (item.type === "noRisk")
                      return (item.value || "").toUpperCase() === "NO"
                        ? 100
                        : 0;

                    const input = parseFloat(item.value) || 0;
                    if (item.below)
                      return (
                        Math.max(0, (item.threshold - input) / item.threshold) *
                        100
                      );
                    else
                      return (
                        Math.max(0, (input - item.threshold) / item.threshold) *
                        100
                      );
                  };

                  const totalWeighted = metrics.reduce((sum, item) => {
                    const sub = computeSubScore(item);
                    return sum + sub * item.weight;
                  }, 0);

                  // ✅ Attach computed value for sorting
                  return { ...data, weighted_points: totalWeighted };
                })}
                enableSorting
                initialState={{
                  pagination: { pageSize: 200 },
                  sorting: [
                    {
                      id: "weighted_points", // the accessorKey of the column to sort by
                      desc: true, // descending order
                    },
                  ],
                }}
                muiTableBodyRowProps={{
                  sx: {
                    height: "20px", // compact row height
                  },
                }}
                muiTableBodyCellProps={{
                  sx: {
                    padding: "2px 4px", // compact cell padding
                  },
                }}
                muiTableHeadCellProps={{
                  sx: {
                    padding: "2px 4px", // same as body cells
                    height: "30px", // or less
                  },
                }}
              />
              ;
            </div>
          </div>
        </div>
      </div>
      <RiskDetailModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        data={selectedRoleData}
      />
    </>
  );
}

export default WorkstationList;
